import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { getProfile } from '../api/profile'
import { getUserMenu } from '../api/rbac'
import type { MenuItem } from '../types/rbac'

interface AuthState {
  profile: any | null
  roles: string[]
  roleIds: number[]
  permissions: string[]
  menu: MenuItem[]
  profileLoading: boolean
  profileError: string | null
  menuLoading: boolean
}

const getStoredProfile = (): any => {
  try {
    const stored = localStorage.getItem('gowardhan_erp_profile')
    return stored ? JSON.parse(stored) : null
  } catch {
    return null
  }
}

const extractRoles = (data: any): string[] => {
  if (!data) return []
  if (Array.isArray(data.roles) && data.roles.length > 0) {
    return data.roles.map((r: any) => (typeof r === 'object' ? r?.name || String(r) : String(r))).filter(Boolean)
  }
  if (data.role) {
    const rName = typeof data.role === 'object' ? data.role?.name || String(data.role) : String(data.role)
    return rName ? [rName] : []
  }
  if (data.user?.role) {
    const rName = typeof data.user.role === 'object' ? data.user.role?.name || String(data.user.role) : String(data.user.role)
    return rName ? [rName] : []
  }
  return []
}

const storedProf = getStoredProfile()

const initialState: AuthState = {
  profile: storedProf,
  roles: extractRoles(storedProf),
  roleIds: storedProf?.roleIds || [],
  permissions: storedProf?.permissions || [],
  menu: [],
  profileLoading: false,
  profileError: null,
  menuLoading: false,
}

// Async thunk — fetch profile with roles and permissions
export const fetchProfile = createAsyncThunk(
  'auth/fetchProfile',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getProfile()
      const data = response.data?.profile ?? response.data
      return data
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch profile')
    }
  }
)

// Async thunk — fetch dynamic menu for logged in user
export const fetchUserMenuThunk = createAsyncThunk(
  'auth/fetchUserMenu',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getUserMenu()
      return response.data || []
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch menu')
    }
  }
)

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setProfile(state, action) {
      const data = action.payload
      state.profile = data
      state.roles = extractRoles(data)
      state.roleIds = Array.isArray(data?.roleIds) ? data.roleIds : []
      state.permissions = Array.isArray(data?.permissions) ? data.permissions : []
      state.profileError = null
      try {
        localStorage.setItem('gowardhan_erp_profile', JSON.stringify(data))
      } catch (e) {
        console.error('Failed to save profile to localStorage', e)
      }
    },
    clearAuth(state) {
      state.profile = null
      state.roles = []
      state.roleIds = []
      state.permissions = []
      state.menu = []
      state.profileLoading = false
      state.profileError = null
      localStorage.removeItem('gowardhan_erp_profile')
      localStorage.removeItem('gowardhan_erp_token')
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProfile.pending, (state) => {
        state.profileLoading = true
        state.profileError = null
      })
      .addCase(fetchProfile.fulfilled, (state, action) => {
        state.profileLoading = false
        const data = action.payload
        state.profile = data
        state.roles = extractRoles(data)
        state.roleIds = Array.isArray(data?.roleIds) ? data.roleIds : []
        state.permissions = Array.isArray(data?.permissions) ? data.permissions : []
        try {
          localStorage.setItem('gowardhan_erp_profile', JSON.stringify(data))
        } catch (e) {
          console.error('Failed to save profile to localStorage', e)
        }
      })
      .addCase(fetchProfile.rejected, (state, action) => {
        state.profileLoading = false
        state.profileError = action.payload as string
        const lowerErr = String(action.payload || '').toLowerCase()
        if (
          lowerErr.includes('token is invalid') ||
          lowerErr.includes('token is expired') ||
          lowerErr.includes('invalid token') ||
          lowerErr.includes('jwt expired') ||
          lowerErr.includes('unauthorized') ||
          lowerErr.includes('401') ||
          lowerErr.includes('403')
        ) {
          state.profile = null
          state.roles = []
          state.roleIds = []
          state.permissions = []
          localStorage.removeItem('gowardhan_erp_profile')
          localStorage.removeItem('gowardhan_erp_token')
          localStorage.removeItem('gowardhan_erp_session')
        }
      })
      .addCase(fetchUserMenuThunk.pending, (state) => {
        state.menuLoading = true
      })
      .addCase(fetchUserMenuThunk.fulfilled, (state, action) => {
        state.menuLoading = false
        state.menu = action.payload
      })
      .addCase(fetchUserMenuThunk.rejected, (state) => {
        state.menuLoading = false
      })
  },
})

export const { setProfile, clearAuth } = authSlice.actions
export default authSlice.reducer

